import { useState, useRef, useEffect } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { Layout } from "@/components/layouts/Layout"
import { ProfileForm } from "./Form"
import { SecurityInfo } from "./Security"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/motion-tabs"
import { ScrollArea } from "@/components/ui/scroll-area"

const TAB_ORDER = ["profile", "security"]

const slideVariants = {
  enter: (dir) => ({ x: dir > 0 ? 16 : -16, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? -16 : 16, opacity: 0 }),
}

export default function AccountEdit({ user }) {
  const initialTab = new URLSearchParams(window.location.search).get("tab")
  const [activeTab, setActiveTab] = useState(TAB_ORDER.includes(initialTab) ? initialTab : "profile")
  const [scrolled, setScrolled] = useState(false)
  const [hasScrolled, setHasScrolled] = useState(false)
  const [tabsStuck, setTabsStuck] = useState(false)
  const directionRef = useRef(0)
  const scrollContainerRef = useRef(null)
  const nameRef = useRef(null)
  const tabsRef = useRef(null)

  useEffect(() => {
    const root = scrollContainerRef.current
    const target = nameRef.current
    if (!root || !target) return

    const observer = new IntersectionObserver(
      ([entry]) => setScrolled(!entry.isIntersecting),
      { root, threshold: 0 }
    )

    observer.observe(target)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const root = scrollContainerRef.current
    const tabs = tabsRef.current
    if (!root || !tabs) return

    const observer = new IntersectionObserver(
      ([entry]) => setTabsStuck(!entry.isIntersecting),
      { root, threshold: 0 }
    )

    observer.observe(tabs)
    return () => observer.disconnect()
  }, [])

  function handleScrollToTop() {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" })
  }

  function handleScroll(e) {
    setHasScrolled(e.currentTarget.scrollTop > 0)
  }

  function handleTabChange(tab) {
    const oldIndex = TAB_ORDER.indexOf(activeTab)
    const newIndex = TAB_ORDER.indexOf(tab)
    directionRef.current = newIndex > oldIndex ? 1 : -1
    setActiveTab(tab)
    setScrolled(false)
    setHasScrolled(false)
    setTabsStuck(false)
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: "instant" })
    const url = new URL(window.location.href)
    url.searchParams.set("tab", tab)
    window.history.replaceState({}, "", url)
  }

  return (
    <Layout insetClassName="overflow-hidden">

      <header className={`flex items-center justify-between gap-4 px-4 h-16 shrink-0 transition-[width,height,border-color] ease-out group-has-data-[collapsible=icon]/sidebar-wrapper:h-12 border-b ${hasScrolled ? "border-border" : "border-transparent"}`}>

        <div className="flex items-center gap-1">
          <AnimatePresence>
            {scrolled && (
              <motion.button
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                onClick={handleScrollToTop}
                className="flex items-center gap-1.5 min-w-0 rounded-md px-2 h-7 hover:bg-accent transition-colors cursor-pointer"
              >
                <span className="text-sm font-medium truncate">Account</span>
              </motion.button>
            )}
          </AnimatePresence>
        </div>

        <div className="flex items-center">
          <AnimatePresence>
            {tabsStuck && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
              >
                <Tabs value={activeTab} onValueChange={handleTabChange}>
                  <TabsList>
                    <TabsTrigger value="profile" className="cursor-pointer">Profile</TabsTrigger>
                    <TabsTrigger value="security" className="cursor-pointer">Security</TabsTrigger>
                  </TabsList>
                </Tabs>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div />
      </header>

      <ScrollArea className="flex-1 min-h-0" viewportRef={scrollContainerRef} onScroll={handleScroll}>
        <div className="flex flex-col gap-8 px-6 pb-6">

          <div ref={nameRef}>
            <h1 className="text-3xl font-medium">Account</h1>
          </div>

          <div ref={tabsRef}>
            <Tabs value={activeTab} onValueChange={handleTabChange}>
              <TabsList>
                <TabsTrigger value="profile">Profile</TabsTrigger>
                <TabsTrigger value="security">Security</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <div className="relative overflow-x-hidden px-1 -mx-1 py-1 -my-1">
            <AnimatePresence initial={false} mode="popLayout" custom={directionRef.current}>
              {activeTab === "profile" && (
                <motion.div
                  key="profile"
                  custom={directionRef.current}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.2, ease: "easeOut" }}
                >
                  <ProfileForm user={user} submitLabel="Save" />
                </motion.div>
              )}

              {activeTab === "security" && (
                <motion.div
                  key="security"
                  custom={directionRef.current}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.2, ease: "easeOut" }}
                >
                  <SecurityInfo user={user} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

        </div>
      </ScrollArea>

    </Layout>
  )
}
